/**
 * Der Partiezustand als Datenformat — hin und zurück.
 *
 * Die Engine rechnet mit `Map`s, weil sie Nachschlagen in konstanter Zeit braucht. Datenbank
 * und WebSocket brauchen **reines JSON**. Diese Datei ist die einzige Stelle, an der beide
 * Welten sich berühren, und sie macht drei Dinge dabei explizit:
 *
 * 1. **`Map` wird Objekt oder Liste.** `JSON.stringify(new Map())` ergibt `{}` — ein stiller
 *    Datenverlust, der erst beim Laden auffällt. Ein Test prüft deshalb, dass die
 *    serialisierte Form einen vollständigen JSON-Umlauf unbeschadet übersteht.
 * 2. **`undefined` wird `null`.** JSON kennt kein `undefined`; ein Feld würde einfach
 *    verschwinden. Beim Lesen wird daraus wieder `undefined`, damit die Engine ihre eigenen
 *    Typen sieht und nicht zwei Arten von „nicht gesetzt" unterscheiden muss.
 * 3. **Abgeleitetes wird weggelassen.** Die Feldbelegung (`occupancy`) ergibt sich aus den
 *    Figuren und wird beim Laden neu berechnet — gespeichert wäre sie eine zweite Wahrheit.
 * 4. **Die Uhr wird mitgespeichert, inklusive Startzeitpunkt.** Eine laufende Uhr ohne ihr
 *    `since` würde beim Laden neu ansetzen, und der Spieler am Zug bekäme bei jedem
 *    Serverneustart die verstrichene Zeit geschenkt. Deshalb ist die Zeit hier ein Wert und
 *    kein Intervall (`ARCHITECTURE.md` E8).
 *
 * **Form und Inhalt werden getrennt geprüft.** Das zod-Schema prüft die Form: Status aus der
 * Aufzählung, Koordinaten ganzzahlig, keine unbekannten Felder. Ob der Zustand *stimmig* ist —
 * keine zwei Figuren auf einem Feld, keine Preview von einem Spieler, der nicht am Zug ist —
 * entscheidet die Engine über `createBoardState` und `restoreGameState`. Ein Schema kennt keine
 * Spielregeln, und beides zu vermischen würde die Regeln verdoppeln.
 *
 * **Zum RuleConfig-Snapshot:** gelesen wird er über `parseRuleConfig`, also mit den heutigen
 * Defaults für Felder, die es beim Speichern noch nicht gab. Für eine Partie, die diese Felder
 * nie kannte, ist das die einzige Möglichkeit — aber es heißt auch, dass ein alter Snapshot
 * nicht exakt dasselbe Spiel beschreibt wie damals. Deshalb gehört die **Engine-Version** zu
 * jeder gespeicherten Partie (`docs/ARCHITECTURE.md`, Phase 6).
 */
import { z } from 'zod';
import { PIECE_TYPES, PLAYER_TYPES, SECTOR_IDS, createBoardState, hex, restoreGameState, } from '@flaghex/engine';
import { parseRuleConfig, ruleConfigSchema } from './rule-config.js';
/**
 * Die Partiezustände aus §47 — eine Liste, damit auch die Lobby-DTOs sie benutzen können,
 * statt sie abzuschreiben.
 */
export const GAME_STATUSES = [
    'CREATED',
    'LOBBY',
    'SETUP',
    'PLAYING',
    'PAUSED',
    'FINISHED',
    'ABORTED',
];
/**
 * Die Gründe, aus denen eine Partie endet (§26).
 *
 * **Seit Zyklus R1 drei statt vier.** `MOST_POINTS` und `LONGEST_SURVIVED` galten für „keine
 * Seite hat mehr Zeit" — ein Zustand, der nicht entstehen kann (siehe `engine/victory.ts`).
 * Dafür ist `LEADER_OUT_OF_TIME` dazugekommen: der einzige Grund, zu dem es **keinen Sieger**
 * gibt.
 */
export const VICTORY_REASONS = [
    'POINT_TARGET',
    'LAST_WITH_TIME',
    'LEADER_OUT_OF_TIME',
];
/**
 * Eine Axialkoordinate. Ganzzahlig — alles andere ist kein Feld (`ARCHITECTURE.md` E15).
 *
 * **Exportiert seit `V2-8a`**, weil das Bot-Protokoll die Aufstellung mitschickt und ein
 * zweites Hex-Schema daneben eine zweite Wahrheit über dieselbe Form wäre.
 */
export const hexSchema = z.strictObject({
    q: z.number().int(),
    r: z.number().int(),
});
const pieceTypeSchema = z.enum(PIECE_TYPES);
const placedPieceSchema = z.strictObject({
    id: z.string().min(1),
    type: pieceTypeSchema,
    owner: z.string().min(1),
    at: hexSchema,
});
/**
 * Eine geschlagene Figur — dieselbe Figur, nur ohne Feld (§22).
 *
 * **Exportiert seit `V2-8a`:** `SetupOptions.pieces` *sind* Vorratsfiguren, und das
 * Bot-Protokoll schickt sie einem fernen Worker.
 */
export const capturedPieceSchema = z.strictObject({
    id: z.string().min(1),
    type: pieceTypeSchema,
    owner: z.string().min(1),
});
/**
 * Eine **verdeckte** Figur (`R17`, Nr. 55): Kennung, Besitzer, Feld — **ohne Art**. Streng, damit
 * eine Art, die doch mitreisen wollte, an der Form scheitert und nicht still mitfährt.
 */
const coveredPieceSchema = z.strictObject({
    id: z.string().min(1),
    owner: z.string().min(1),
    at: hexSchema,
});
const boardStateSchema = z.strictObject({
    pieces: z.array(placedPieceSchema),
    captured: z.array(capturedPieceSchema),
});
const playerSchema = z.strictObject({
    id: z.string().min(1),
    sector: z.enum(SECTOR_IDS),
    /** `null` heißt: kein Team — 1:1 und FFA. */
    team: z.string().min(1).nullable(),
    /**
     * Seit `V2-2a` `BOT` statt `LLM` (`v2Prompt.md` §31): ein LLM ist eine **Strategie**, kein
     * Spielertyp. Welche Strategie, steht nicht im Zustand — siehe `PlayerType` in der Engine.
     *
     * **Der Satz kommt seit `V2-2d` aus der Engine** (`PLAYER_TYPES`) und steht hier nicht mehr
     * als Literal. Dieselbe Liste setzt die Datenbank als `CHECK`-Bedingung durch; drei Quellen
     * für denselben Satz wären drei Stellen, die auseinanderlaufen können.
     */
    playerType: z.enum(PLAYER_TYPES),
});
/**
 * Die laufende Uhr: wem sie gehört und seit wann.
 *
 * `since` ist ein Zeitstempel in Millisekunden. Er wird **mitgespeichert**, nicht beim Laden
 * neu gesetzt — sonst bekäme der Spieler am Zug bei jedem Serverneustart die bisher
 * verstrichene Zeit geschenkt (`ARCHITECTURE.md` E8).
 */
const runningClockSchema = z.strictObject({
    player: z.string().min(1),
    since: z.number().int(),
});
/**
 * Die Schachuhr einer Zeitpartie (§27, §38).
 *
 * `remaining` als **Objekt** und nicht als Liste von Paaren: dieselbe Begründung wie bei den
 * Punkten — so kann JSONB direkt auf einen Spieler zugreifen. Werte in Millisekunden,
 * nie negativ.
 */
const timerSchema = z.strictObject({
    /**
     * Das konfigurierte Guthaben je Spieler (§42) — eine **Einstellung**, kein Stand.
     *
     * Steht neben `remaining`, weil es daraus nicht ablesbar ist, sobald die Uhr gelaufen ist —
     * und weil eine Lobby ohne zugeordnete Spieler (Nr. 31) gar keine Restzeiten hat.
     */
    budgetMs: z.number().int().positive(),
    remaining: z.record(z.string().min(1), z.number().int().min(0)),
    /** `null` heißt: niemandes Uhr läuft — vor dem ersten Zug, in der Pause, am Ende. */
    running: runningClockSchema.nullable(),
});
const pendingActionSchema = z.strictObject({
    player: z.string().min(1),
    actionId: z.string().min(1),
});
/**
 * Wie eine Partie ausgegangen ist.
 *
 * **`winner: null` heißt Unentschieden** (Zyklus R1) — und nicht „noch nicht entschieden": das
 * steht im `outcome` selbst, das dann ganz fehlt. Die beiden Fälle sind verschieden und werden
 * hier verschieden geschrieben.
 */
const outcomeSchema = z.strictObject({
    winner: z.string().min(1).nullable(),
    reason: z.enum(VICTORY_REASONS),
});
/**
 * Die gespeicherte und übertragene Form einer Partie.
 *
 * `strictObject` wie beim RuleConfig-Schema: ein stillschweigend ignoriertes Feld wäre ein
 * Datenverlust, den niemand bemerkt.
 */
export const gameStateSchema = z.strictObject({
    status: z.enum(GAME_STATUSES),
    /**
     * **Darf leer sein** (Nr. 31): in der Lobby ist vorübergehend niemand einer Farbe
     * zugeordnet. Dass das ab `SETUP` nicht mehr gilt, prüft die Engine beim Laden — das Schema
     * prüft die Form, die Stimmigkeit bleibt beim Rechenkern.
     */
    players: z.array(playerSchema),
    turnOrder: z.array(z.string().min(1)),
    activePlayer: z.string().min(1).nullable(),
    turnNumber: z.number().int().min(0),
    eliminated: z.array(z.string().min(1)),
    /** Punkte je Spieler-ID — als Objekt, damit JSONB sie direkt indexieren kann. */
    scores: z.record(z.string().min(1), z.number().int()),
    victory: z.enum(['POINTS', 'TIME']),
    boardState: boardStateSchema,
    /** `null` in Variante A — der Normalfall, dort gibt es keine Uhr (Nr. 22). */
    timer: timerSchema.nullable(),
    pendingAction: pendingActionSchema.nullable(),
    outcome: outcomeSchema.nullable(),
    /**
     * Wer seine Aufstellung bestätigt hat (§10, Nr. 33).
     *
     * Muss mitreisen: ohne dieses Feld wäre nach einem Serverneustart jede Bestätigung
     * vergessen, und ein Spieler könnte seine Aufstellung noch einmal ändern. Außerhalb von
     * `SETUP` ist die Liste leer.
     */
    setupConfirmed: z.array(z.string().min(1)),
    /** Wann die Aufstellungszeit abläuft (§11). `null` heißt **unbegrenzt** (§42, Default). */
    setupDeadlineAt: z.number().int().nullable(),
    /**
     * Die fremden Figuren **verdeckt** — nur in einer Projektion der Aufstellung (`R17`). Fehlt sonst
     * ganz, damit gespeicherte Partien Byte für Byte gleich bleiben.
     */
    coveredPieces: z.array(coveredPieceSchema).optional(),
    ruleConfig: ruleConfigSchema,
});
function serializePlayer(player) {
    return {
        id: player.id,
        sector: player.sector,
        team: player.team ?? null,
        playerType: player.playerType,
    };
}
/** Bringt eine Partie in die Form, die gespeichert und übertragen wird. */
export function serializeGameState(state) {
    return {
        status: state.status,
        players: state.players.map(serializePlayer),
        turnOrder: [...state.turnOrder],
        activePlayer: state.activePlayer ?? null,
        turnNumber: state.turnNumber,
        eliminated: [...state.eliminated],
        scores: Object.fromEntries(state.scores),
        victory: state.victory,
        boardState: {
            pieces: [...state.boardState.pieces.values()].map((piece) => ({
                id: piece.id,
                type: piece.type,
                owner: piece.owner,
                at: { q: piece.at.q, r: piece.at.r },
            })),
            captured: [...state.boardState.captured.values()].map((piece) => ({
                id: piece.id,
                type: piece.type,
                owner: piece.owner,
            })),
        },
        timer: state.timer === undefined
            ? null
            : {
                budgetMs: state.timer.budgetMs,
                remaining: Object.fromEntries(state.timer.remaining),
                running: state.timer.running === undefined
                    ? null
                    : { player: state.timer.running.player, since: state.timer.running.since },
            },
        setupConfirmed: [...state.setupConfirmed],
        setupDeadlineAt: state.setupDeadlineAt ?? null,
        ...(state.coveredPieces === undefined
            ? {}
            : {
                coveredPieces: state.coveredPieces.map((piece) => ({
                    id: piece.id,
                    owner: piece.owner,
                    at: { q: piece.at.q, r: piece.at.r },
                })),
            }),
        pendingAction: state.pendingAction === undefined
            ? null
            : { player: state.pendingAction.player, actionId: state.pendingAction.actionId },
        outcome: state.outcome === undefined
            ? null
            : { winner: state.outcome.winner, reason: state.outcome.reason },
        ruleConfig: state.ruleConfig,
    };
}
/**
 * Liest eine gespeicherte Partie zurück — Form über das Schema, Stimmigkeit über die Engine.
 *
 * Wirft bei beidem: eine formal falsche Eingabe weist zod ab, eine formal richtige aber
 * inhaltlich korrupte die Engine-Konstruktoren. Beides soll laut scheitern, damit eine
 * beschädigte Zeile nicht als halbe Partie weiterläuft.
 */
export function deserializeGameState(raw) {
    const parsed = gameStateSchema.parse(raw);
    const boardState = createBoardState({
        pieces: parsed.boardState.pieces.map((piece) => ({
            id: piece.id,
            type: piece.type,
            owner: piece.owner,
            at: hex(piece.at.q, piece.at.r),
        })),
        captured: parsed.boardState.captured.map((piece) => ({
            id: piece.id,
            type: piece.type,
            owner: piece.owner,
        })),
    });
    return restoreGameState({
        players: parsed.players.map((player) => ({
            id: player.id,
            sector: player.sector,
            team: player.team ?? undefined,
            playerType: player.playerType,
        })),
        turnOrder: parsed.turnOrder,
        victory: parsed.victory,
        ruleConfig: parseRuleConfig(parsed.ruleConfig),
        status: parsed.status,
        activePlayer: parsed.activePlayer ?? undefined,
        turnNumber: parsed.turnNumber,
        eliminated: parsed.eliminated,
        scores: new Map(Object.entries(parsed.scores)),
        boardState,
        timer: parsed.timer === null
            ? undefined
            : {
                budgetMs: parsed.timer.budgetMs,
                remaining: new Map(Object.entries(parsed.timer.remaining)),
                running: parsed.timer.running === null
                    ? undefined
                    : { player: parsed.timer.running.player, since: parsed.timer.running.since },
            },
        pendingAction: parsed.pendingAction ?? undefined,
        outcome: parsed.outcome ?? undefined,
        setupConfirmed: parsed.setupConfirmed,
        setupDeadlineAt: parsed.setupDeadlineAt ?? undefined,
        ...(parsed.coveredPieces === undefined
            ? {}
            : {
                coveredPieces: parsed.coveredPieces.map((piece) => ({
                    id: piece.id,
                    owner: piece.owner,
                    at: hex(piece.at.q, piece.at.r),
                })),
            }),
    });
}
//# sourceMappingURL=game-state.js.map